/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */

import React, { useState, useEffect } from 'react';
import { Card, CardFooter, Form } from 'reactstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { compact } from 'lodash';
import { parse, endOfDay, subYears, startOfDay } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { all, spread } from 'axios';
import { Button, FormAlerts, useFeatures } from '@cordata/buffet';
import { authorizedInstance as axios } from '~/utils';

import PatientsAddFields from './Fields';

const serializeEmail = (e, p) => ({
  data: {
    ...e,
    type: 'email-addresses',
    email_address: e.emailAddress,
    links: {
      patient: {
        linkage: {
          type: 'patients',
          id: p,
        },
      },
    },
  },
});

const serializeAddress = (a, p) => ({
  data: {
    ...a,
    type: 'addresses',
    address1: a.address1,
    address2: a.address2,
    state: a.state && a.state.attributes.divisionAbbrevIso,
    address_type_uid: a.addressType && a.addressType.id,
    is_primary: a.isPrimary || false,
    is_in_use: true,
    post_code: a.postCode,
    links: {
      patient: {
        linkage: {
          type: 'patients',
          id: p,
        },
      },
    },
  },
});

const initialValues = {
  referralDate: new Date(),
  referredTo: 'Program',
  requestedBy: 'Provider',
  emails: [{}],
  mobilePhone: '',
  homePhone: '',
  workPhone: '',
};

const PatientsAddForm = () => {
  const { hasAppCommunity } = useFeatures();
  const [isBirthDateRequired, setIsBirthDateRequired] = useState(!hasAppCommunity);
  const history = useHistory();

  useEffect(() => {
    // because useFeatures doesn't return instantly
    setIsBirthDateRequired(!hasAppCommunity);
  }, [hasAppCommunity]);

  return (
    <Formik
      displayName="PatientsAddForm"
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={Yup.object().shape({
        firstName: Yup.string().required(),
        lastName: Yup.string().required(),
        birthDate: isBirthDateRequired
          ? Yup.date().required()
          : Yup.date().nullable(),
        gender: Yup.mixed().required(),
        referralDate: Yup.date()
          .min(subYears(startOfDay(new Date()), 10))
          .max(endOfDay(new Date()))
          .required(),
        referralReason: Yup.mixed().required(),
        requestedBy: Yup.string().required(),
        referredTo: Yup.string().required(),
        primaryPhone: Yup.string().required(),
        mobilePhone: Yup.mixed().when('primaryPhone', {
          is: p => p === 'mobile',
          then: Yup.mixed().required(),
          otherwise: Yup.mixed().notRequired(),
        }),
        homePhone: Yup.mixed().when('primaryPhone', {
          is: p => p === 'home',
          then: Yup.mixed().required(),
          otherwise: Yup.mixed().notRequired(),
        }),
        workPhone: Yup.mixed().when('primaryPhone', {
          is: p => p === 'work',
          then: Yup.mixed().required(),
          otherwise: Yup.mixed().notRequired(),
        }),
        emails: Yup.array().of(Yup.object().shape({
            emailAddress: Yup.string()
              .nullable()
              .email(),
          })),
      })}
      initialValues={initialValues}
      onSubmit={(values, { setErrors, setSubmitting }) => {
        const p = {
          data: {
            first_name: values.firstName,
            last_name: values.lastName,
            birth_date: values.birthDate
              ? parse(values.birthDate).toISOString()
              : null,
            gender_uid: values.gender.genderUid,
            gender_identity_uid: values.genderIdentity?.genderIdentityUid,
            mobile_phone:
              values.mobilePhone && values.mobilePhone !== ''
                ? values.mobilePhone.replace(/\D/g, '')
                : null,
            home_phone:
              values.homePhone && values.homePhone !== ''
                ? values.homePhone.replace(/\D/g, '')
                : null,
            work_phone:
              values.workPhone && values.workPhone !== ''
                ? values.workPhone.replace(/\D/g, '')
                : null,
            primary_phone_number: values.primaryPhone,
            event_date: parse(new Date()).toISOString(),
            requested_by: values.requestedBy,
            referral_source_uid: values.referralSource?.referralSourceUid,
            is_inbound_referral: true,
            referred_to: values.referredTo,
            preferred_engagement: 'No Preference',
            links: {
              gender: {
                linkage: {
                  id: values.gender.genderUid,
                },
              },
              genderIdentity: {
                linkage: {
                  id: values.genderIdentity?.genderIdentityUid,
                },
              },
              reason: {
                linkage: {
                  id: values.referralReason.id,
                },
              },
            },
          },
        };

        axios
          .post('referrals/inbound', p)
          .then(r => r.data.data)
          .then(r => {
            const addressPromises =
              values.addresses && values.addresses.length
                ? values.addresses.map(a =>
                    axios.post(
                      '/addresses',
                      serializeAddress(a, r.attributes.patientUid),
                    ))
                : [];

            const emailPromises = compact(values.emails.map(e => e.emailAddress)).length
              ? values.emails.map(e =>
                  axios.post(
                    '/email-addresses',
                    serializeEmail(e, r.attributes.patientUid),
                  ))
              : [];

            return all([r, ...addressPromises, ...emailPromises]);
          })
          .then(spread(patient => patient))
          .then(r => history.push(`/patients/${r.attributes.patientUid}`))
          .catch(e => setErrors({ server: e.errors }))
          .then(() => setSubmitting(false));
      }}
    >
      {({
 handleSubmit, isSubmitting, submitCount, errors,
}) => (
  <Card>
    <Form onSubmit={handleSubmit} noValidate>
      {submitCount >= 1 && errors && <FormAlerts errors={errors} />}
      <PatientsAddFields />

      <CardFooter className="d-flex">
        <div className="ml-auto">
          <Button
            type="cancel"
            disabled={isSubmitting}
            onClick={history.goBack}
          >
            Cancel
          </Button>

          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Working ...' : 'Save'}
          </Button>
        </div>
      </CardFooter>
    </Form>
  </Card>
      )}
    </Formik>
  );
};

export default PatientsAddForm;
